import { useState } from 'react';
import { fetchAdAgentQualificationDetailReport } from '../../../network/opm-ad';
import { useDispatch } from 'react-redux';
import { AgentQualificationDetailReport } from '../../../types/opm-ad-types';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AppPaperTitle from 'src/app/common/components/AppPaperTitle';
import AppPaperContent from 'src/app/common/components/AppPaperContent';
import { PruTableHeader } from 'src/app/common/components/Table/PruTableHeader';
import { PruTableRow } from 'src/app/common/components/Table/PruTableRow';
import { formatDateTime, formatNumber } from '../../../helpers/common.helper';

const SearchQualificationAgentPage = () => {
  const [agentCodeSearch, setAgentCodeSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const [agentQualificationDetailReport, setAgentQualificationDetailReport] =
    useState<AgentQualificationDetailReport | null>(null);

  const dispatch = useDispatch();

  const fetchAdAgentQualificationDetailReportData = async () => {
    if (!agentCodeSearch.trim()) {
      return;
    }
    setSearching(true);
    try {
      const data = await fetchAdAgentQualificationDetailReport(agentCodeSearch.trim(), dispatch);
      setAgentQualificationDetailReport(data);
    } catch (_) {
      setAgentQualificationDetailReport(null);
    } finally {
      setSearching(false);
    }
  };

  const handleReset = () => {
    setAgentCodeSearch('');
  };

  const report = agentQualificationDetailReport;

  return (
    <>
      <Paper sx={{ mb: 2 }}>
        <AppPaperTitle>Agent Qualification Detail Report filter</AppPaperTitle>
        <AppPaperContent sx={{ pt: 0 }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body1" color="textSecondary">
                Agent Code:
              </Typography>
              <TextField
                sx={{
                  my: 0,
                }}
                color="secondary"
                variant="outlined"
                value={agentCodeSearch}
                disabled={searching}
                onChange={(e) => setAgentCodeSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    fetchAdAgentQualificationDetailReportData();
                  }
                }}
              />
            </Box>
            <Box flex={1} />
            <Box display="flex" alignItems="center" gap={1}>
              <Button variant="contained" color="inherit" onClick={handleReset} disabled={searching}>
                Reset
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  if (!searching && agentCodeSearch.trim()) {
                    fetchAdAgentQualificationDetailReportData();
                  }
                }}
                disabled={searching}
                startIcon={searching ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {searching ? 'Searching...' : 'Search'}
              </Button>
            </Box>
          </Box>
        </AppPaperContent>
      </Paper>

      <Paper>
        <AppPaperTitle>Agent Qualification Detail Report list</AppPaperTitle>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <PruTableHeader>Agent Number</PruTableHeader>
                <PruTableHeader>AG Name</PruTableHeader>
                <PruTableHeader>AG Category</PruTableHeader>
                <PruTableHeader>Appointed Date</PruTableHeader>
                <PruTableHeader align="right" sx={{ minWidth: 120 }}>
                  Number of Serving IF Policies
                </PruTableHeader>
                <PruTableHeader align="center">DC (Y/N)</PruTableHeader>
                <PruTableHeader align="center">Complaint (Y/N)</PruTableHeader>
                <PruTableHeader align="center">Maternity Leave (Y/N)</PruTableHeader>
                <PruTableHeader align="right">Persistency (%)</PruTableHeader>
                <PruTableHeader>Product License</PruTableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {searching ? (
                <PruTableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} color="secondary" />
                  </TableCell>
                </PruTableRow>
              ) : report ? (
                <PruTableRow>
                  <TableCell>{report.agentCode}</TableCell>
                  <TableCell>{report.fullname || '-'}</TableCell>
                  <TableCell>{report.agCat || '-'}</TableCell>
                  <TableCell>{report.joinDate ? formatDateTime(report.joinDate, 'DD/MM/YYYY') : '-'}</TableCell>
                  <TableCell align="right">{formatNumber(report.opm?.numOfCarePolicy)}</TableCell>
                  <TableCell align="center">
                    {report.opm?.legalStatus === 'TRUE' ? (
                      <Typography color="success.main">Y</Typography>
                    ) : (
                      <Typography color="error.main">N</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {report.opm?.isComplaint === 'Y' ? (
                      <Typography color="success.main">Y</Typography>
                    ) : (
                      <Typography color="error.main">N</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {report.opm?.isMatLeave === 'Y' ? (
                      <Typography color="success.main">Y</Typography>
                    ) : (
                      <Typography color="error.main">N</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{formatNumber(report.opm?.persistency14m, 1)}</TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(6, max-content)',
                        gap: 0.5,
                      }}
                    >
                      {report.opm?.licenseProduct?.map((licenceProduct, index) => (
                        <span key={index}>
                          {licenceProduct}
                          {index < report.opm.licenseProduct.length - 1 && report.opm.licenseProduct.length > 1
                            ? ', '
                            : ''}
                        </span>
                      )) || '-'}
                    </Box>
                  </TableCell>
                </PruTableRow>
              ) : (
                <PruTableRow>
                  <TableCell colSpan={10} align="center" sx={{ height: 200 }}>
                    No record to display
                  </TableCell>
                </PruTableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
};

export default SearchQualificationAgentPage;
